import { Badge, Button, Card, CardContent, cn, Spinner } from '@crm/ui';
import { CreditCard, Percent } from 'lucide-react';
import { useState } from 'react';
import { ConfirmDialog, PageHeader } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { useCan } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import {
  type PaymentMachine,
  useAcquirers,
  useCreateMachine,
  useDeleteMachine,
  useMachines,
  useUpdateMachine,
} from '../api';
import { MachineRulesDialog } from '../components/machine-rules-dialog';

/**
 * O salao nao cadastra maquininha: marca quais usa. A tabela de taxas ja nasce
 * preenchida com os valores mais comuns e fica a um toque de ajuste.
 */
export function MachinesPage() {
  useDocumentTitle('Maquininhas');

  const canWrite = useCan('finance', 'WRITE');
  const acquirers = useAcquirers();
  const machines = useMachines();

  const createMachine = useCreateMachine();
  const updateMachine = useUpdateMachine();
  const deleteMachine = useDeleteMachine();

  const [editingRules, setEditingRules] = useState<PaymentMachine | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<PaymentMachine | null>(null);

  const inUse = new Map(
    (machines.data?.data ?? []).map((machine) => [machine.acquirerId, machine]),
  );
  const mutationError = createMachine.error ?? updateMachine.error ?? deleteMachine.error;

  function toggle(acquirerId: string) {
    const machine = inUse.get(acquirerId);

    if (!machine) {
      createMachine.mutate({ acquirerId, isActive: true });
      return;
    }

    // Com venda lancada nao da para excluir: desativa e some das opcoes.
    if (machine.usageCount > 0) {
      updateMachine.mutate({ id: machine.id, input: { isActive: !machine.isActive } });
      return;
    }

    setPendingRemoval(machine);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<CreditCard />}
        title="Maquininhas"
        description="Marque as que o salao usa. As taxas ja vem preenchidas - ajuste so o que for diferente."
      />

      {mutationError ? (
        <p className="text-sm text-destructive">{toErrorMessage(mutationError)}</p>
      ) : null}

      {/* Primeira vez: dois passos, menos de dois minutos, sem wizard. */}
      {!machines.isLoading && inUse.size === 0 ? (
        <Card className="border-primary/40 bg-primary/5">
          <CardContent className="space-y-2 p-4 sm:p-5">
            <p className="font-medium">Configure em dois minutos</p>
            <ol className="list-inside list-decimal space-y-1 text-sm text-muted-foreground">
              <li>Toque nas maquininhas que o salao usa.</li>
              <li>
                Confira as taxas no icone de porcentagem - so mude o que for diferente do seu
                contrato.
              </li>
            </ol>
            <p className="text-sm text-muted-foreground">
              Pronto: cada venda passa a gerar as parcelas sozinha, com data e taxa.
            </p>
          </CardContent>
        </Card>
      ) : null}

      {acquirers.isLoading || machines.isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner className="h-6 w-6 text-muted-foreground" />
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
          {(acquirers.data?.data ?? []).map((acquirer) => {
            const machine = inUse.get(acquirer.id);
            const isOn = Boolean(machine?.isActive);

            return (
              <Card
                key={acquirer.id}
                className={cn(
                  'transition-colors',
                  isOn ? 'border-primary/50 bg-primary/5' : 'bg-muted/20',
                )}
              >
                <CardContent className="flex items-center justify-between gap-3 p-4">
                  <button
                    type="button"
                    disabled={!canWrite}
                    onClick={() => toggle(acquirer.id)}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                  >
                    <span
                      className={cn(
                        'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border',
                        isOn
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-background text-muted-foreground',
                      )}
                    >
                      <CreditCard className="h-5 w-5" />
                    </span>

                    <span className="min-w-0">
                      <span className="block truncate font-medium">{acquirer.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {machine
                          ? machine.isActive
                            ? `${machine.usageCount} venda(s)`
                            : 'Desativada'
                          : 'Toque para usar'}
                      </span>
                    </span>
                  </button>

                  {machine ? (
                    <div className="flex shrink-0 items-center gap-1">
                      {machine.isActive ? <Badge variant="secondary">Em uso</Badge> : null}
                      {canWrite ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Taxas e prazos"
                          onClick={() => setEditingRules(machine)}
                        >
                          <Percent className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <MachineRulesDialog machine={editingRules} onClose={() => setEditingRules(null)} />

      <ConfirmDialog
        open={pendingRemoval !== null}
        title="Parar de usar"
        message={`A ${pendingRemoval?.label ?? ''} sai da lista de formas de pagamento. Como nao ha venda lancada nela, nada se perde.`}
        confirmLabel="Parar de usar"
        loading={deleteMachine.isPending}
        onClose={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (pendingRemoval) deleteMachine.mutate(pendingRemoval.id);
          setPendingRemoval(null);
        }}
      />
    </div>
  );
}
